/**
 * Ce que « la fiche est faite » veut dire.
 *
 * Le point délicat est de ne pas fabriquer une file qui ne se vide jamais.
 * Toutes les cases ne sont pas exigibles partout :
 *
 *  - le **rôle** l'est toujours — c'est lui qui décide du reste ;
 *  - une société classée **bruit** s'arrête là : lui demander un profil
 *    d'achat et une cadence de relance n'a aucun sens ;
 *  - le **groupe** (tier) porte la cadence de relance : on ne l'exige donc
 *    que de qui se relance — client, fournisseur, négociant. Une
 *    administration ou un transporteur ne se relance pas à intervalle, et
 *    leur réclamer une cadence bloquait la fiche sans issue ;
 *  - le **profil d'achat** ne décrit que la façon d'acheter : il ne concerne
 *    que qui achète (client, négociant), et sur les autres il ne doit pas
 *    même s'afficher. Un transporteur n'a pas de mode d'achat, et lui
 *    montrer une case vide donne à lire une donnée manquante là où il n'y a
 *    rien à saisir. D'où `relevant`, distinct de `required` ;
 *  - **étiquettes**, **organigramme** et **pays** sont affichés mais jamais
 *    exigés. Toutes les sociétés n'ont pas de TRUCK à cocher ni d'acheteur
 *    nommé, et en faire une obligation rendrait 1 800 fiches éternellement
 *    rouges. Le pays se travaille par sa propre file (« Sans pays »), qui
 *    est le bon outil pour une donnée de renseignement plutôt que de
 *    décision.
 *
 * Une seule fonction, pour que la règle se change à un seul endroit.
 */

import type { Company } from "./types";

export type Slot = "role" | "profile" | "group" | "country" | "tags" | "org";

export const SLOT_LABELS: Record<Slot, string> = {
  role: "Rôle",
  profile: "Profil",
  group: "Groupe",
  country: "Pays",
  tags: "Étiquettes",
  org: "Organigramme",
};

export interface Completeness {
  filled: Record<Slot, boolean>;
  /**
   * Les cases qui ONT UN SENS pour cette société — celles que la fiche
   * affiche et que la pastille compte. Un axe hors sujet n'apparaît pas du
   * tout, plutôt que d'apparaître vide.
   */
  relevant: Slot[];
  /** Sous-ensemble de `relevant` : ce qu'il faut avoir tranché. */
  required: Slot[];
  missing: Slot[];
  done: boolean;
}

export function classificationOf(c: Company): string[] {
  return Array.isArray(c.classification) ? c.classification : [];
}

export function isNoise(c: Company): boolean {
  const cls = classificationOf(c);
  return cls.length > 0 && cls.every((v) => v === "noise");
}

/**
 * Les relations que le radar relance à intervalle — les seules dont on
 * exige un groupe. Un partenaire, une administration ou un transporteur
 * peuvent en recevoir un (la section reste affichée), mais ne pas en avoir
 * n'est pas une lacune. Exporté pour que la file « Sans groupe » applique
 * exactement la même règle que la complétude.
 */
export function needsTier(c: Company): boolean {
  const cls = classificationOf(c);
  if (cls.length === 0 || isNoise(c)) return false;
  return (
    cls.includes("customer") ||
    cls.includes("intermediary") ||
    cls.includes("supplier")
  );
}

export function completeness(
  c: Company,
  tagCount: number,
  functionCount: number,
): Completeness {
  const cls = classificationOf(c);

  const filled: Record<Slot, boolean> = {
    role: cls.length > 0,
    profile: !!c.buyer_profile,
    group: !!c.tier,
    country: !!c.country_code,
    tags: tagCount > 0,
    org: functionCount > 0,
  };

  const noise = isNoise(c);
  // Une fiche déclarée classée sort des files : plus rien n'est exigé
  // d'elle, c'est tout l'intérêt du geste.
  const reviewed = !!c.reviewed_at;
  // Le profil ne concerne que qui achète. Les 761 clients du portefeuille
  // face aux 72 non-clients hors bruit : c'est un axe client, pas un axe
  // général.
  const buys = cls.includes("customer") || cls.includes("intermediary");

  const recurring = needsTier(c);

  const relevant: Slot[] = ["role"];
  if (buys) relevant.push("profile");
  // Le bruit ne se relance pas et son pays n'intéresse personne ; le reste
  // (étiquettes, organigramme) peut servir sur n'importe quelle fiche.
  if (!noise) relevant.push("group", "country");
  relevant.push("tags", "org");

  const required: Slot[] = reviewed ? [] : ["role"];
  if (!reviewed && filled.role && !noise) {
    if (recurring) required.push("group");
    if (buys) required.push("profile");
  }

  const missing = required.filter((s) => !filled[s]);
  return {
    filled,
    relevant,
    required,
    missing,
    done: reviewed || missing.length === 0,
  };
}
